import { useReactiveVar } from '@apollo/client';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import currencyVar from '../../../apollo/reactive-variables/money';
import { DocumentList } from '../../../components/lists';
import FactoringHeader from '../../../components/headers/FactoringHeader';
import HeaderSearcherInput from '../../../components/inputs/HeaderSearcherInput';
import {
  OUTSIDE_BOX_PROPS_FOR_HORIZONTAL_SCROLL_VISIBLE,
  DETAILED_DATA_GRID_CLASS_NAME,
} from '../../../theme/constants/data-grid';
import CLIENT_ASSIGNMENTS from '../../../graphql/queries/factoring/client-assignments';
import { FACTORING_DOCUMENT_FINANCE_STATE_ADDITIONAL_QUERY } from '../../../constants/factoring-additional-queries';
import useIsMobile from '../../../hooks/useIsMobile';
import getOperationsColumns from '../getOperationsColumns';
import BaseInvoiceDetail from '../components/BaseInvoiceDetail';
import OperationsStatusFilter from '../components/OperationsStatusFilter';

const OperationsStatusView = ({
  trackerId,
  headerTitle,
  headerDescription,
  statusFilter,
  mobileHeaders,
  extraHeaderComponent,
  getCards,
  getAmountCards,
  summaryTableColumns,
  includeHeaders,
  getTrackingSteps,
  showPayments,
  useBorderedTitle,
  showSurplus,
  showRefreshButton,
  showWarnings,
  OverlayComponent,
}) => {
  const isMobile = useIsMobile();
  const currency = useReactiveVar(currencyVar);
  const { path } = useRouteMatch();
  const columns = useMemo(
    () => getOperationsColumns({ isMobile, showWarnings }),
    [isMobile, showWarnings],
  );

  const FiltersComponent = useMemo(() => {
    const OperationsFilters = (filterProps) => (
      <OperationsStatusFilter
        getCards={getCards}
        showSurplus={showSurplus}
        showRefreshButton={showRefreshButton}
        {...filterProps}
      />
    );
    return OperationsFilters;
  }, [getCards, showSurplus, showRefreshButton]);

  const DetailComponent = useMemo(() => {
    const OperationsDetail = () => (
      <BaseInvoiceDetail
        summaryTableColumns={summaryTableColumns}
        getAmountCards={getAmountCards}
        showPayments={showPayments}
        getTrackingSteps={getTrackingSteps}
        showWarnings={showWarnings}
      />
    );
    return OperationsDetail;
  }, [
    summaryTableColumns,
    getAmountCards,
    showPayments,
    getTrackingSteps,
    showWarnings,
  ]);

  return (
    <Switch>
      <Route exact path={path}>
        <DocumentList
          trackerId={trackerId}
          type="operations-status"
          headerTitle={headerTitle}
          queryDocument={CLIENT_ASSIGNMENTS}
          additionalQueries={FACTORING_DOCUMENT_FINANCE_STATE_ADDITIONAL_QUERY}
          initialOrderBy="-dateOperation"
          emitter
          countryFilter
          includeHeaders={includeHeaders}
          mobileHeaders={mobileHeaders}
          allCompaniesOptions
          customVariables={{
            currency,
            hasBeenCeded: true,
            dateIssued_Gte: null,
            dateIssued_Lte: null,
            status: statusFilter,
          }}
          columns={columns}
          dataGridClassName={DETAILED_DATA_GRID_CLASS_NAME}
          DataGridNoRowsOverlayComponent={OverlayComponent}
          extraHeaderComponent={extraHeaderComponent}
          CustomHeader={FactoringHeader}
          customHeaderProps={{
            headerTitle,
            headerDescription,
            useBorderedTitle,
            TitleSideComponent: HeaderSearcherInput,
            Filters: FiltersComponent,
          }}
          outsideBoxProps={OUTSIDE_BOX_PROPS_FOR_HORIZONTAL_SCROLL_VISIBLE}
        />
      </Route>
      <Route path={`${path}/:invoiceId`}>
        <DetailComponent />
      </Route>
    </Switch>
  );
};

OperationsStatusView.propTypes = {
  trackerId: PropTypes.string.isRequired,
  headerTitle: PropTypes.string.isRequired,
  headerDescription: PropTypes.string.isRequired,
  statusFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  mobileHeaders: PropTypes.arrayOf(PropTypes.string),
  extraHeaderComponent: PropTypes.node,
  getCards: PropTypes.func.isRequired,
  getAmountCards: PropTypes.func.isRequired,
  summaryTableColumns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  includeHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  getTrackingSteps: PropTypes.func,
  showPayments: PropTypes.bool,
  useBorderedTitle: PropTypes.bool,
  showSurplus: PropTypes.bool,
  showRefreshButton: PropTypes.bool,
  showWarnings: PropTypes.bool,
  OverlayComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
};

OperationsStatusView.defaultProps = {
  mobileHeaders: null,
  extraHeaderComponent: null,
  getTrackingSteps: () => [],
  showPayments: true,
  useBorderedTitle: false,
  showSurplus: false,
  showRefreshButton: false,
  showWarnings: true,
  OverlayComponent: undefined,
};

export default OperationsStatusView;
